import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useUser, Show } from "@clerk/react";
import { AuthModal } from "@/components/ui/auth-modal";
import { useGenerate } from "@/hooks/use-generation";
import { useUserStore } from "@/stores/user-store";
import { T2I_MODEL, IMAGE_EDIT_MODEL } from "@/config/models";
import { Loader2, ArrowRight, AlertCircle, Upload, X } from "lucide-react";
import { NsfwOrError } from "@/components/ui/nsfw-error";
import { UnsupportedFileModal, checkImageFile, unsupportedReasonFromError, IMAGE_ACCEPT, type UnsupportedReason } from "@/components/ui/unsupported-file-modal";
import { IconCoin } from "@/components/ui/cartoon-icons";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { trackViewContent } from "@/services/analytics";

const DRAFT_KEY = "cartoontale_draft_image";

function saveDraft(imageUrl: string, prompt: string) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ imageUrl, prompt }));
  } catch {}
}

function loadDraft(): { imageUrl: string; prompt: string } | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && (parsed.imageUrl || parsed.prompt)) return parsed;
  } catch {}
  return null;
}

function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); } catch {}
}

export function CreateCharacterPage() {
  const navigate = useNavigate();
  const { user, isSignedIn } = useUser();
  const { credits } = useUserStore();
  const generateMutation = useGenerate();
  const draft = loadDraft();
  const [prompt, setPrompt] = useState(draft?.prompt || "");
  const [imageUrl, setImageUrl] = useState(draft?.imageUrl || "");
  const [uploading, setUploading] = useState(false);
  const [unsupported, setUnsupported] = useState<UnsupportedReason | null>(null);
  const [showAuth, setShowAuth] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasImage = imageUrl.length > 0;
  const activeModel = hasImage ? IMAGE_EDIT_MODEL : T2I_MODEL;
  const canGenerate = prompt.trim().length > 0 && credits >= activeModel.creditCost;

  useEffect(() => {
    trackViewContent("Create Character", user?.id, { funnel_stage: "create" });
  }, []);

  useEffect(() => {
    saveDraft(imageUrl, prompt);
  }, [imageUrl, prompt]);

  const triggerUpload = useCallback(() => {
    if (!isSignedIn) { setShowAuth(true); return; }
    fileInputRef.current?.click();
  }, [isSignedIn]);

  const handleUpload = useCallback(async (file: File) => {
    if (!user?.id) { setShowAuth(true); return; }
    const unsupportedReason = checkImageFile(file);
    if (unsupportedReason) { setUnsupported(unsupportedReason); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.upload<{ success: boolean; url: string }>("/upload", formData);
      if (res.url) setImageUrl(res.url);
    } catch (err) {
      console.error("Upload failed:", err);
    } finally {
      setUploading(false);
    }
  }, [user?.id]);

  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) return;
    try {
      const payload = hasImage
        ? { model_id: IMAGE_EDIT_MODEL.id, prompt: prompt.trim(), input_url: imageUrl }
        : { model_id: T2I_MODEL.id, prompt: prompt.trim() };
      const result = await generateMutation.mutateAsync(payload);
      if (result.video?.id) {
        clearDraft();
        navigate(`/generation/${result.video.id}`);
      }
    } catch (err) { setUnsupported(unsupportedReasonFromError(err)); console.error("Generation failed:", err); }
  }, [prompt, imageUrl, hasImage, generateMutation, navigate]);

  return (
    <div className="max-w-xl mx-auto px-5 md:px-8 pt-8 pb-16">
      <div className="mb-6">
        <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide text-[#7c5cf0] bg-[#b8a5f0]/15 border border-[#b8a5f0]/30 rounded-full px-2.5 py-1 mb-2">
          Text to Image &middot; Image Edit
        </span>
        <h1 className="text-2xl md:text-3xl font-black text-[#241d3a] tracking-tight mb-1">
          {hasImage ? "EDIT CHARACTER" : "CREATE CHARACTER"}
        </h1>
        <p className="text-[14px] text-[#241d3a]/60 font-medium">
          {hasImage ? "Upload an image and describe how you want it changed." : "Describe a character from text and AI will generate them."}
        </p>
      </div>

      <div className="space-y-4">
        <div
          className={cn(
            "rounded-2xl border-2 p-5 text-center transition-all cursor-pointer",
            hasImage
              ? "border-[#2a2342] bg-white shadow-[2px_2px_0_rgba(42,35,66,0.16)]"
              : "border-dashed border-[#241d3a]/15 bg-[#241d3a]/[0.04] hover:bg-[#241d3a]/[0.08] hover:border-[#241d3a]/25",
          )}
          onClick={() => !hasImage && triggerUpload()}
        >
          {hasImage ? (
            <div className="relative inline-block">
              <img src={imageUrl} alt="Uploaded" className="max-h-48 mx-auto rounded-xl" />
              <button
                onClick={(e) => { e.stopPropagation(); setImageUrl(""); }}
                className="absolute -top-2 -right-2 size-6 rounded-full bg-[#241d3a] text-white flex items-center justify-center hover:bg-red-500 transition-colors"
              >
                <X className="size-3" strokeWidth={3} />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 py-2">
              {uploading ? (
                <Loader2 className="size-6 animate-spin text-[#241d3a]/45" />
              ) : (
                <>
                  <Upload className="size-6 text-[#241d3a]/35" />
                  <p className="text-[13px] text-[#241d3a]/45 font-bold">Upload a character to edit <span className="text-[#241d3a]/35">(optional)</span></p>
                </>
              )}
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={IMAGE_ACCEPT}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUpload(file);
            }}
          />
        </div>

        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
          placeholder={hasImage
            ? "Change the character's outfit to a superhero costume, add a cape, make the colors more vibrant..."
            : "A cheerful fox wearing a top hat and monocle, standing on hind legs, Pixar-style, big expressive eyes..."}
          rows={4} className="input-field resize-none" />

        {!hasImage && (
          <div className="flex flex-wrap gap-2">
            {["Superhero", "Cute animal", "Robot companion", "Fairy princess", "Pirate captain", "Space explorer", "Wizard", "Chibi style"].map((tag) => (
              <button key={tag} type="button" onClick={() => setPrompt((p) => p ? `${p}, ${tag.toLowerCase()}` : tag.toLowerCase())}
                className="pill text-[11px] py-1.5 px-3.5 cursor-pointer">
                {tag}
              </button>
            ))}
          </div>
        )}

        {isSignedIn && (
          <div className="flex items-center justify-between text-[13px] text-[#241d3a]/60 font-bold pt-1">
            <span className="inline-flex items-center gap-1"><IconCoin className="size-3.5" />{activeModel.creditCost} tokens</span>
            <span className="tabular-nums">Balance: <span className={credits >= activeModel.creditCost ? "text-[#241d3a]" : "text-red-500"}>{credits.toLocaleString()}</span></span>
          </div>
        )}

        {isSignedIn && credits > 0 && credits < activeModel.creditCost && (
          <div className="flex items-center gap-2 text-[13px] font-bold text-red-500"><AlertCircle className="size-4 shrink-0" />Not enough tokens. <Link to="/billing" className="underline">Get more</Link></div>
        )}

        <Show when="signed-out">
          <button onClick={() => setShowAuth(true)} className="btn-accent w-full py-3.5 text-[15px] flex items-center justify-center gap-2">
            {hasImage ? "Edit Character" : "Create Character"} <ArrowRight className="size-4" />
          </button>
      <UnsupportedFileModal reason={unsupported} onClose={() => setUnsupported(null)} onChooseAnother={() => fileInputRef.current?.click()} />
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
        </Show>
        <Show when="signed-in">
          <button onClick={handleGenerate} disabled={!canGenerate || generateMutation.isPending} className="btn-accent w-full py-3.5 text-[15px] flex items-center justify-center gap-2">
            {generateMutation.isPending
              ? <><Loader2 className="size-4 animate-spin" />{hasImage ? "Editing..." : "Creating..."}</>
              : <>{hasImage ? "Edit Character" : "Create Character"} <ArrowRight className="size-4" /></>}
          </button>
        </Show>

        {generateMutation.isError && <NsfwOrError error={generateMutation.error} />}
      </div>
    </div>
  );
}
